"use client";
import {
  useState as ReactUseState,
  useEffect as ReactUseEffect,
  useContext as ReactUseContext,
  useReducer as ReactUseReducer,
  useMemo as ReactUseMemo,
  useCallback as ReactUseCallback,
  useRef as ReactUseRef,
  useLayoutEffect as ReactUseLayoutEffect,
  useImperativeHandle as ReactUseImperativeHandle,
  useDebugValue as ReactUseDebugValue,
  useTransition as ReactUseTransition,
  useDeferredValue as ReactUseDeferredValue,
  useId as ReactUseId,
  memo as ReactMemo,
} from "react";

import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/utils/redux/store";
import { useFilter, useToggleFilter } from "@dxp-feed/algolia";

/**
 * Custom useState hook
 * @template T Type of state
 * @param initialValue Initial state value
 * @returns [state, setState] Tuple containing state and updater function
 */
export const useState = <T>(initialValue: T | (() => T)) => {
  return ReactUseState(initialValue);
};

/**
 * Custom useEffect hook
 * @param effect Effect callback function
 * @param deps Optional dependency list
 */
export const useEffect = (
  effect: React.EffectCallback,
  deps?: React.DependencyList
) => {
  ReactUseEffect(effect, deps);
};

/**
 * Custom useContext hook
 * @template T Type of context value
 * @param context React context object
 * @returns Context value
 */
export const useContext = <T>(context: React.Context<T>) => {
  return ReactUseContext(context);
};

/**
 * Custom useReducer hook
 * @template S Type of state
 * @template A Type of action
 * @param reducer Reducer function
 * @param initialState Initial state value
 * @param init Optional initializer function
 * @returns [state, dispatch] Tuple containing state and dispatch function
 */
export const useReducer = <S, A>(
  reducer: React.Reducer<S, A>,
  initialState: S,
  init: (initialState: S) => S
) => {
  return ReactUseReducer(reducer, initialState, init);
};

/**
 * Custom useMemo hook
 * @template T Return type of factory function
 * @param factory Factory function
 * @param deps Dependency list
 * @returns Memoized value
 */
export const useMemo = <T>(factory: () => T, deps: React.DependencyList) => {
  return ReactUseMemo(factory, deps);
};

/**
 * Custom useCallback hook
 * @template T Type of callback function
 * @param callback Function to memoize
 * @param deps Dependency list
 * @returns Memoized callback
 */

/**
 * Custom memo hook
 * @template T Component type
 * @param Component React component to be memoized
 * @param areEqual Optional comparison function for memoization
 * @returns Memoized component
 */

export const useCallback = <T extends (...args: any[]) => any>(
  callback: T,
  deps: React.DependencyList
) => {
  return ReactUseCallback(callback, deps);
};

/**
 * Custom useRef hook
 * @template T Type of ref value
 * @param initialValue Initial value for the ref
 * @returns Mutable ref object
 */
export const useRef = <T>(initialValue: T | null) => {
  return ReactUseRef(initialValue);
};

/**
 * Custom useLayoutEffect hook
 * @param effect Effect callback function
 * @param deps Optional dependency list
 */
export const useLayoutEffect = (
  effect: React.EffectCallback,
  deps?: React.DependencyList
) => {
  ReactUseLayoutEffect(effect, deps);
};

/**
 * Custom useImperativeHandle hook
 * @template T Type of ref value
 * @template R Return type of createHandle function
 * @param ref Forwarded ref
 * @param createHandle Function to create ref handle
 * @param deps Dependency list
 */
export const useImperativeHandle = <T, R extends T>(
  ref: React.Ref<T>,
  createHandle: () => R,
  deps?: React.DependencyList
) => {
  ReactUseImperativeHandle(ref, createHandle, deps);
};

/**
 * Custom useDebugValue hook
 * @template T Type of value
 * @param value Value to display in React DevTools
 * @param formatFunction Optional formatting function
 */
export const useDebugValue = <T>(
  value: T,
  formatFunction?: (value: T) => string
) => {
  ReactUseDebugValue(value, formatFunction);
};

/**
 * Custom useTransition hook
 * @returns [isPending, startTransition] Tuple containing pending state and startTransition function
 */
export const useTransition = () => {
  return ReactUseTransition();
};

/**
 * Custom useDeferredValue hook
 * @template T Type of deferred value
 * @param value Value to defer
 * @returns Deferred value
 */
export const useDeferredValue = <T>(value: T) => {
  return ReactUseDeferredValue(value);
};

/**
 * Custom useId hook
 * @returns Unique ID
 */
export const useId = () => {
  return ReactUseId();
};

/**
 * Custom hook for typed `useDispatch` to dispatch actions in Redux.
 * Ensures that the dispatch function is type-safe, using `AppDispatch` from the store.
 */
export const useAppDispatch = () => useDispatch<AppDispatch>();

/**
 * Custom hook for typed `useSelector` to access state from the Redux store.
 * Enforces type safety by using the `RootState` type from the store.
 */
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/**
 * Custom memo hook
 * @template T Component type
 * @param Component React component to be memoized
 * @param areEqual Optional comparison function for memoization
 * @returns Memoized component
 */
export const useMemoComponent = <T extends React.ComponentType<any>>(
  Component: T,
  areEqual?: (
    prevProps: Readonly<React.ComponentProps<T>>,
    nextProps: Readonly<React.ComponentProps<T>>
  ) => boolean
): React.MemoExoticComponent<T> => {
  return ReactMemo(Component, areEqual);
};
const allergenAttributes = [
  "allergens.alcohol",
  "allergens.eggFree",
  "allergens.glutenFree",
  "allergens.halal",
  "allergens.kosher",
  "allergens.lactoseFree",
  "allergens.milkFree",
  "allergens.mustardFree",
  "allergens.nutFree",
  "allergens.oatFree",
  "allergens.organic",
  "allergens.peanutFree",
  "allergens.sesameFree",
  "allergens.shellfishFree",
  "allergens.soyaFree",
  "allergens.sulphitesFree",
  "allergens.vegan",
  "allergens.vegetarian",
  "allergens.wheatFree",
];

export function useAllFilters(offersAttribute:string) {
  const allergenFilters = allergenAttributes.reduce(
    (acc, attr) => {
      acc[attr] = useToggleFilter({ attribute: attr });
      return acc;
    },
    {} as Record<string, ReturnType<typeof useToggleFilter>>
  );
  const specialAttributesMap: Record<string, string> = {
  brand: "brand",
  offertypes: offersAttribute,
};
  const specialFilters = Object.entries(specialAttributesMap).reduce(
    (acc, [key, attr]) => {
      acc[key] = useFilter({ attribute: attr });
      return acc;
    },
    {} as Record<string, ReturnType<typeof useFilter>>
  );
  const filters: Record<string, any> = {
    ...allergenFilters,
    ...specialFilters
  };
  function getFilter(attribute: string) {
    return filters[attribute];
  }
  return { filters, getFilter };
}